<template>
  <div>
    <!-- Hero Banner Slider -->
    <HeroSlider />

    <!-- Best Sellers Section -->
    <section id="featured-products" class="bg-gray-100 dark:bg-gray-900 py-24 md:py-40">
      <div class="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <!-- Section Header -->
        <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Featured Products
            </h2>
            <p class="text-gray-600 dark:text-gray-400 text-sm">
              Discover our premium selection of supplements
            </p>
          </div>
          <RouterLink 
            to="/products" 
            class="inline-flex items-center gap-2 text-sm font-semibold hover:gap-3 transition-all cursor-pointer group"
            style="color: #5f4842;"
          >
            View all products
            <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </RouterLink>
        </div>

        <!-- Category Filters (oculto si solo hay una categoría) -->
        <div v-if="categories.length > 1" class="flex items-center gap-3 mb-10 overflow-x-auto pb-2 scrollbar-hide">
          <button 
            v-for="category in categories" 
            :key="category"
            @click="selectedCategory = category"
            :class="[
              'px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300',
              selectedCategory === category 
                ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg scale-105' 
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
            ]"
          >
            {{ category }}
          </button>
        </div>
        <div v-else class="mb-10" aria-hidden="true"></div>

        <!-- Loading State -->
        <div v-if="productsLoading" class="text-center py-12">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p class="mt-4 text-gray-600">Loading products...</p>
        </div>

        <!-- Empty State: no products at the moment -->
        <div v-else-if="featuredProducts.length === 0" class="text-center py-16 px-4">
          <div class="max-w-md mx-auto">
            <div class="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <svg class="w-10 h-10 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8 4-8-4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">No products at the moment</h3>
            <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
              We're currently updating our selection. New premium supplements will be available soon—check back later.
            </p>
            <RouterLink
              to="/products"
              class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style="background-color: #EDD97A; color: #1a1200;"
            >
              View all products
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </RouterLink>
          </div>
        </div>

        <!-- Products Slider Container -->
        <div v-else class="relative">
          <!-- Slider Wrapper -->
          <div class="overflow-hidden">
            <div 
              class="flex transition-transform duration-500 ease-out"
              :style="{ transform: `translateX(-${currentProductSlide * 100}%)` }"
            >
              <!-- Dynamic Slides - 4 products per slide -->
              <div 
                v-for="slideIndex in slidesArray" 
                :key="slideIndex"
                class="min-w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <!-- Products from API -->
                <div 
                  v-for="(product, index) in featuredProducts.slice(slideIndex * 4, (slideIndex + 1) * 4)" 
                  :key="product.id"
                  class="product-card bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 group relative min-h-[400px]"
                  :class="{ 'animate-fade-in-up': isFeaturedVisible }"
                  :style="`animation-delay: ${(slideIndex * 4 + index) * 100}ms`"
                >
                  <div class="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 p-8 flex items-center justify-center overflow-hidden relative">
                    <!-- Shimmer effect on hover -->
                    <div class="shimmer-overlay"></div>
                    <img 
                      :src="product.image" 
                      :alt="product.name"
                      class="w-full h-full object-contain group-hover:scale-105 transition-all duration-500"
                    />
                    
                    <!-- Quick Actions Overlay -->
                    <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button @click="goToProduct(product.id)" class="bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-3 rounded-full hover:scale-110 transition-transform shadow-lg cursor-pointer" title="Ver detalle">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div class="p-5">
                    <div class="mb-3">
                      <h3 class="font-bold text-gray-900 dark:text-white mb-1 text-base group-hover:text-black dark:group-hover:text-white transition-colors">
                        {{ product.name }}
                      </h3>
                      <p class="text-sm text-gray-500 dark:text-gray-400">{{ product.category }}</p>
                    </div>
                    
                    <!-- Rating Stars -->
                    <div class="flex items-center gap-1 mb-4">
                      <div class="flex items-center gap-0.5">
                        <svg v-for="star in 5" :key="star" class="w-4 h-4" :class="star <= Math.round(product.rating) ? 'fill-yellow-400' : 'fill-gray-300 dark:fill-gray-600'" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                        </svg>
                      </div>
                      <span class="text-xs text-gray-600 dark:text-gray-400 font-medium ml-1">{{ product.rating }} ({{ product.reviews }})</span>
                    </div>
                    
                    <div class="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div class="flex flex-col">
                        <span class="text-2xl font-bold text-gray-900 dark:text-white">${{ product.price }}</span>
                        <span class="text-xs" :class="product.stock > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                          {{ product.stock > 0 ? 'In stock' : 'Out of Stock' }}
                        </span>
                      </div>
                      <button 
                        @click="addToCart(product.id)" 
                        :disabled="product.stock === 0"
                        :class="[
                          'px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-105 cursor-pointer shadow-lg',
                          product.stock > 0 
                            ? 'bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100' 
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        ]"
                      >
                        {{ product.stock > 0 ? 'Add' : 'Out of Stock' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Navigation Arrows -->
          <button 
            v-if="currentProductSlide > 0 && featuredProducts.length > 4"
            @click="prevProductSlide"
            class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 text-white p-4 rounded-full transition-all cursor-pointer z-20 shadow-2xl hover:scale-110"
            style="background-color: #EDD97A;"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            v-if="currentProductSlide < totalSlides - 1"
            @click="nextProductSlide"
            class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 text-white p-4 rounded-full transition-all cursor-pointer z-20 shadow-2xl hover:scale-110"
            style="background-color: #EDD97A;"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <!-- Slider Indicators (Dots) -->
        <div v-if="featuredProducts.length > 4" class="flex items-center justify-center gap-3 mt-10">
          <button
            v-for="index in slidesArray"
            :key="index"
            @click="currentProductSlide = index"
            :class="[
              'transition-all duration-300',
              currentProductSlide === index
                ? 'w-10 h-3 rounded-full' 
                : 'w-3 h-3 bg-gray-300 dark:bg-gray-600 rounded-full hover:bg-gray-400 dark:hover:bg-gray-500'
            ]"
            :style="currentProductSlide === index ? 'background-color: #EDD97A;' : ''"
            :aria-label="`Ir a slide ${index + 1}`"
          />
        </div>

      </div>
    </section>

    <!-- Promotional Banner -->
    <section class="relative bg-black py-16 md:py-24 overflow-hidden">
      <!-- Video Background -->
      <div class="absolute inset-0 w-full h-full">
        <video 
          autoplay 
          muted 
          loop 
          playsinline
          class="w-full h-full object-cover"
        >
          <source src="/logos/video-banner.mp4" type="video/mp4">
        </video>
        <!-- Overlay for better text readability -->
        <div class="absolute inset-0 bg-black/40"></div>
      </div>
      
      <!-- Content -->
      <div class="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div class="text-center">
          <!-- Main Promotional Text -->
          <div class="max-w-4xl mx-auto">
            <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Power Your <span class="text-gray-300">Performance</span><br/>
              To The Next Level
            </h2>
            
            <p class="text-2xl md:text-3xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed cormorant-text">
              From the Earth to you!
            </p>

          </div>

        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section id="faq-section" class="py-16 md:py-24 bg-white dark:bg-black">
      <div class="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <div class="inline-block mb-4">
            <span 
              class="text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-full transition-all duration-700"
              :class="{ 'category-fade-up': isCategoriesVisible }"
              style="background-color: rgba(176, 140, 122, 0.1); color: #EDD97A;"
            >
              FAQ
            </span>
          </div>
          <h2 
            class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 transition-all duration-700"
            :class="{ 'category-fade-up': isCategoriesVisible }"
            style="transition-delay: 0.1s;"
          >
            Frequently Asked Questions
          </h2>
          <p 
            class="text-lg text-gray-600 dark:text-gray-400 transition-all duration-700"
            :class="{ 'category-fade-up': isCategoriesVisible }"
            style="transition-delay: 0.2s;"
          >
            Everything you need to know about our products and services
          </p>
        </div>

        <!-- FAQ Accordion -->
        <div class="space-y-4">
          <!-- FAQ Item 1 -->
          <div 
            class="faq-item group border-2 rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-xl"
            :class="[
              { 'category-fade-up': isCategoriesVisible },
              openFaq === 0 
                ? 'border-[#EDD97A] shadow-lg' 
                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            ]"
            style="transition-delay: 0.3s;"
          >
            <button
              @click="toggleFaq(0)"
              class="w-full flex items-center justify-between p-6 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 cursor-pointer"
            >
              <span class="text-lg font-semibold text-gray-900 dark:text-white pr-8 group-hover:translate-x-1 transition-transform duration-300">
                What are SupplemEarth Greens?
              </span>
              <div 
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                :class="openFaq === 0 ? 'scale-110' : ''"
                :style="openFaq === 0 ? 'background-color: #EDD97A;' : 'background-color: rgba(176, 140, 122, 0.1);'"
              >
                <svg 
                  :class="{ 'rotate-45': openFaq === 0 }" 
                  class="w-5 h-5 transition-transform duration-300" 
                  :style="openFaq === 0 ? 'color: white;' : 'color: #EDD97A;'"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-if="openFaq === 0" class="px-6 pb-6 bg-gray-50 dark:bg-gray-800/50">
                <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
                 SupplemEarth Greens is a premium greens powder made with 30 earth-sourced ingredients designed to support digestion, immune health, energy, and overall wellness.
It’s an easy way to add more nutrients to your daily routine with a refreshing taste you can mix with water, smoothies, or your favorite non-citrus juice.

                </p>
              </div>
            </Transition>
          </div>

          <!-- FAQ Item 2 -->
          <div 
            class="faq-item group border-2 rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-xl"
            :class="[
              { 'category-fade-up': isCategoriesVisible },
              openFaq === 1 
                ? 'border-[#EDD97A] shadow-lg' 
                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            ]"
            style="transition-delay: 0.4s;"
          >
            <button
              @click="toggleFaq(1)"
              class="w-full flex items-center justify-between p-6 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 cursor-pointer"
            >
              <span class="text-lg font-semibold text-gray-900 dark:text-white pr-8 group-hover:translate-x-1 transition-transform duration-300">
                How do I take SupplemEarth Greens?
              </span>
              <div 
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                :class="openFaq === 1 ? 'scale-110' : ''"
                :style="openFaq === 1 ? 'background-color: #EDD97A;' : 'background-color: rgba(176, 140, 122, 0.1);'"
              >
                <svg 
                  :class="{ 'rotate-45': openFaq === 1 }" 
                  class="w-5 h-5 transition-transform duration-300" 
                  :style="openFaq === 1 ? 'color: white;' : 'color: #EDD97A;'"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-if="openFaq === 1" class="px-6 pb-6 bg-gray-50 dark:bg-gray-800/50">
                <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
Simply mix one scoop daily with 8–12 oz of water, smoothies, protein shakes, or your favorite non-citrus juice.
For best results, enjoy it in the morning as part of your daily wellness routine.
                </p>
              </div>
            </Transition>
          </div>

          <!-- FAQ Item 3 -->
          <div 
            class="faq-item group border-2 rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-xl"
            :class="[
              { 'category-fade-up': isCategoriesVisible },
              openFaq === 2 
                ? 'border-[#EDD97A] shadow-lg' 
                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            ]"
            style="transition-delay: 0.5s;"
          >
            <button
              @click="toggleFaq(2)"
              class="w-full flex items-center justify-between p-6 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 cursor-pointer"
            >
              <span class="text-lg font-semibold text-gray-900 dark:text-white pr-8 group-hover:translate-x-1 transition-transform duration-300">
                Do you test your products for quality and safety?
              </span>
              <div 
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                :class="openFaq === 2 ? 'scale-110' : ''"
                :style="openFaq === 2 ? 'background-color: #EDD97A;' : 'background-color: rgba(176, 140, 122, 0.1);'"
              >
                <svg 
                  :class="{ 'rotate-45': openFaq === 2 }" 
                  class="w-5 h-5 transition-transform duration-300" 
                  :style="openFaq === 2 ? 'color: white;' : 'color: #EDD97A;'"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-if="openFaq === 2" class="px-6 pb-6 bg-gray-50 dark:bg-gray-800/50">
                <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
Yes. Each batch undergoes rigorous quality testing during manufacturing, and a Certificate of Analysis (COA) is generated to verify purity, ingredient identity, and safety. Our products are manufactured in an NSF-certified GMP facility and an FDA-registered facility.
                </p>
              </div>
            </Transition>
          </div>

          <!-- FAQ Item 4 -->
          <div 
            class="faq-item group border-2 rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-xl"
            :class="[
              { 'category-fade-up': isCategoriesVisible },
              openFaq === 3 
                ? 'border-[#EDD97A] shadow-lg' 
                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            ]"
            style="transition-delay: 0.6s;"
          >
            <button
              @click="toggleFaq(3)"
              class="w-full flex items-center justify-between p-6 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 cursor-pointer"
            >
              <span class="text-lg font-semibold text-gray-900 dark:text-white pr-8 group-hover:translate-x-1 transition-transform duration-300">
                What are the shipping cost and delivery times?
              </span>
              <div 
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                :class="openFaq === 3 ? 'scale-110' : ''"
                :style="openFaq === 3 ? 'background-color: #EDD97A;' : 'background-color: rgba(176, 140, 122, 0.1);'"
              >
                <svg 
                  :class="{ 'rotate-45': openFaq === 3 }" 
                  class="w-5 h-5 transition-transform duration-300" 
                  :style="openFaq === 3 ? 'color: white;' : 'color: #EDD97A;'"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-if="openFaq === 3" class="px-6 pb-6 bg-gray-50 dark:bg-gray-800/50">
                <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
We currently ship within the United States only.
Free shipping on orders over $45.
Orders are typically delivered in about 5 business days. Once your order ships, you will receive a tracking number via email.
Please ensure your shipping address is entered correctly at checkout. SupplemEarth is not responsible for orders shipped to incorrectly provided addresses.               </p>
              </div>
            </Transition>
          </div>

          <!-- FAQ Item 5 -->
          <div 
            class="faq-item group border-2 rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-xl"
            :class="[
              { 'category-fade-up': isCategoriesVisible },
              openFaq === 4 
                ? 'border-[#EDD97A] shadow-lg' 
                : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
            ]"
            style="transition-delay: 0.7s;"
          >
            <button
              @click="toggleFaq(4)"
              class="w-full flex items-center justify-between p-6 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 cursor-pointer"
            >
              <span class="text-lg font-semibold text-gray-900 dark:text-white pr-8 group-hover:translate-x-1 transition-transform duration-300">
              Can I return or exchange my products?
              </span>
              <div 
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110" 
                :class="openFaq === 4 ? 'scale-110' : ''"
                :style="openFaq === 4 ? 'background-color: #EDD97A;' : 'background-color: rgba(176, 140, 122, 0.1);'"
              >
                <svg 
                  :class="{ 'rotate-45': openFaq === 4 }" 
                  class="w-5 h-5 transition-transform duration-300" 
                  :style="openFaq === 4 ? 'color: white;' : 'color: #EDD97A;'"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </button>
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              enter-from-class="max-h-0 opacity-0"
              enter-to-class="max-h-96 opacity-100"
              leave-from-class="max-h-96 opacity-100"
              leave-to-class="max-h-0 opacity-0"
            >
              <div v-if="openFaq === 4" class="px-6 pb-6 bg-gray-50 dark:bg-gray-800/50">
                <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
Unopened and undamaged products may be returned within 15 days of delivery for a full refund. Please note that customers are responsible for return shipping costs.

At this time, glass bottles and promotional items are final sale and cannot be returned or exchanged.

If you would like to request a return, please contact us through our Contact Us page and our team will assist you.                </p>
              </div>
            </Transition>
          </div>
        </div>

        <!-- Contact CTA -->
        <div 
          class="mt-12 text-center p-8 bg-gray-50 dark:bg-gray-900 rounded-2xl transition-all duration-700"
          :class="{ 'category-fade-up': isCategoriesVisible }"
          style="transition-delay: 0.8s;"
        >
          <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Still have questions?
          </h3>
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            Our support team is here to help you 24/7
          </p>
          <button
            type="button"
            @click="openContactModal"
            class="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg cursor-pointer hover:opacity-90"
            style="background-color: #EDD97A; color: #1a1200;"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contact Support
          </button>
        </div>
      </div>
    </section>

    <!-- Video Banner Section -->
    <section id="video-banner" class="pt-8 pb-6 md:pt-24 md:pb-12 bg-gray-100 dark:bg-gray-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div class="relative rounded-2xl sm:rounded-3xl overflow-hidden min-h-[340px] h-[52vh] sm:h-[420px] md:h-[500px] lg:h-[550px] max-h-[520px] sm:max-h-none">
          <!-- Video Background -->
          <video 
            autoplay 
            muted 
            loop 
            playsinline
            class="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/logos/video-banner3.mp4" type="video/mp4">
          </video>
          
          <!-- Dark Overlay with Gradient -->
          <div class="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
          
          <!-- Decorative Lines (ocultos en móvil para más espacio) -->
          <div class="hidden sm:block absolute left-0 top-20 w-1 h-32 bg-gradient-to-b from-gray-400 to-transparent opacity-50"></div>
          <div class="hidden sm:block absolute left-0 top-32 w-20 h-1 bg-gradient-to-r from-gray-400 to-transparent opacity-50"></div>
          
          <!-- Content - Aligned Left -->
          <div class="relative z-10 h-full flex flex-col justify-center py-6 px-4 sm:px-6 md:px-12 lg:px-20">
            <!-- Small Label -->
            <div 
              class="mb-2 sm:mb-4 transition-all duration-700"
              :class="{ 'banner-slide-in-left': isVideoBannerVisible }"
            >
              <span class="text-gray-400 uppercase text-[10px] sm:text-xs md:text-sm font-bold tracking-widest border-l-4 border-gray-400 pl-2 sm:pl-4">
                Power Your Performance
              </span>
            </div>
            
            <!-- Main Heading: tamaño progresivo para que no se recorte en móvil -->
            <h2 
              class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-2 sm:mb-4 md:mb-6 leading-tight max-w-3xl transition-all duration-700"
              :class="{ 'banner-slide-in-left': isVideoBannerVisible }"
              style="transition-delay: 0.1s;"
            >
              <span class="text-white block mb-0.5 sm:mb-2">To The Next</span>
              <span class="text-gray-300 block">Level</span>
            </h2>
            
            <!-- Subheading -->
            <p 
              class="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-3 sm:mb-5 md:mb-8 max-w-xl leading-relaxed transition-all duration-700 line-clamp-2 sm:line-clamp-none"
              :class="{ 'banner-slide-in-left': isVideoBannerVisible }"
              style="transition-delay: 0.2s;"
            >
              <span class="cormorant-text" style="font-size: 1.15em; font-style: italic;">Premium supplements designed for athletes seeking exceptional results. Guaranteed quality, proven results.</span>
            </p>
            
            <!-- CTA Button -->
            <div 
              class="transition-all duration-700"
              :class="{ 'banner-slide-in-left': isVideoBannerVisible }"
              style="transition-delay: 0.4s;"
            >
              <RouterLink
                to="/about"
                class="inline-block px-6 py-2.5 sm:px-8 sm:py-3 md:px-10 md:py-4 bg-white text-black font-bold text-sm sm:text-base md:text-lg rounded-full transition-all duration-300 shadow-2xl hover:shadow-3xl hover:bg-gray-100"
              >
                About Us
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useModalStore } from '@/stores/modal'
import { useProducts } from '@/composables/useProducts'
import { mapApiProductToEcommerce, mapApiProductToCartItem } from '@/utils/productMapper'
import HeroSlider from '@/components/home/HeroSlider.vue'

// Animation states
const isFeaturedVisible = ref(false)
const isVideoBannerVisible = ref(false)
const isCategoriesVisible = ref(false)

// FAQ state
const openFaq = ref<number | null>(null)

const router = useRouter()
const cartStore = useCartStore()
const modalStore = useModalStore()

function openContactModal() {
  modalStore.openContactModal()
}

// Products from API
const { products: apiProducts, loading: productsLoading, fetchProducts } = useProducts()

// Featured products: primeros 8 para el slider (los más recientes / todos)

const featuredProducts = computed(() =>
  [...apiProducts.value]
    .sort((a, b) =>
      (a.Sku || '').localeCompare(b.Sku || '')
    )
    .slice(0, 8)
    .map(mapApiProductToEcommerce)
)

// Add to cart function
const addToCart = (productId: string) => {
  const apiProduct = apiProducts.value.find(p => p.Product_id.toString() === productId)
  if (apiProduct) {
    const cartItem = mapApiProductToCartItem(apiProduct)
    cartStore.addItem(cartItem)
  }
}

// Go to product detail
const goToProduct = (productId: string) => {
  router.push(`/product/${productId}`)
}

// Toggle FAQ function
const toggleFaq = (index: number) => {
  openFaq.value = openFaq.value === index ? null : index
}


onMounted(async () => {
  // Cargar productos de la API
  await fetchProducts()
  await nextTick()
  setupScrollAnimations()
})


// Scroll animations setup
const setupScrollAnimations = () => {
  if (typeof document === 'undefined') return
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'featured-products') {
            isFeaturedVisible.value = true
          } else if (entry.target.id === 'video-banner') {
            isVideoBannerVisible.value = true
          } else if (entry.target.id === 'faq-section') {
            isCategoriesVisible.value = true
          }
        }
      })
    },
    { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
  )

  // Observe sections - usar setTimeout para asegurar que el DOM esté listo
  setTimeout(() => {
    if (typeof document === 'undefined') return
    const featuredSection = document.getElementById('featured-products')
    const videoBannerSection = document.getElementById('video-banner')
    const faqSection = document.getElementById('faq-section')
    
    if (featuredSection) observer.observe(featuredSection)
    if (videoBannerSection) observer.observe(videoBannerSection)
    if (faqSection) observer.observe(faqSection)
  }, 100)
}

// Categories (solo Creatine en la página principal)
const categories = ref(['Creatine'])
const selectedCategory = ref('Creatine')

// Product slider logic
const currentProductSlide = ref(0)

const totalSlides = computed(() => Math.ceil(featuredProducts.value.length / 4))
const slidesArray = computed(() => Array(totalSlides.value).fill(0).map((_, i) => i))

const nextProductSlide = () => {
  const maxSlides = totalSlides.value - 1
  if (currentProductSlide.value < maxSlides) {
    currentProductSlide.value++
  }
}

const prevProductSlide = () => {
  if (currentProductSlide.value > 0) {
    currentProductSlide.value--
  }
}
</script>

<style scoped>
/* Animación de entrada mejorada con bounce */
@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  60% {
    opacity: 1;
    transform: translateY(-5px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

/* Efecto shimmer al hover */
@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(30deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(30deg);
  }
}

.shimmer-overlay {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.product-card:hover .shimmer-overlay {
  opacity: 1;
  animation: shimmer 1.5s ease-in-out;
}

/* Efecto 3D en hover - más suave */
.product-card {
  transform-style: preserve-3d;
  transition: all 0.3s ease-out;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.2);
}

/* Animación para el badge NEW */
@keyframes pulseBadge {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
  }
}

.animate-pulse-badge {
  animation: pulseBadge 2s ease-in-out infinite;
}

/* Mejora en los botones - hover suave */
.product-card button {
  transition: all 0.2s ease;
}

.product-card button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px -2px rgba(0, 0, 0, 0.2);
}

/* Efecto en el overlay de acciones rápidas */
.product-card .absolute.inset-0 {
  backdrop-filter: blur(4px);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Efecto de brillo en las estrellas al hover */
.product-card:hover svg.fill-yellow-400 {
  filter: drop-shadow(0 0 3px rgba(234, 179, 8, 0.6));
  transition: filter 0.3s ease;
}

/* Animación de los precios - más suave */
.product-card:hover .text-2xl {
  transform: scale(1.02);
  transition: transform 0.2s ease;
  display: inline-block;
}

/* Efecto parallax ligero en la imagen */
@media (hover: hover) {
  .product-card:hover img {
    animation: floatImage 3s ease-in-out infinite;
  }
}

@keyframes floatImage {
  0%, 100% {
    transform: translateY(0) scale(1.05);
  }
  50% {
    transform: translateY(-4px) scale(1.06);
  }
}

/* Video Banner Slide In Animation */
.banner-slide-in-left {
  animation: bannerSlideIn 0.8s ease-out forwards;
}

@keyframes bannerSlideIn {
  0% {
    opacity: 0;
    transform: translateX(-80px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Category Cards Fade Up Animation */
.category-fade-up {
  animation: categoryFadeUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes categoryFadeUp {
  0% {
    opacity: 0;
    transform: translateY(60px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Category Card Hover Effects */
.category-card:hover {
  transform: translateY(-8px);
}

/* FAQ Item Animations */
.faq-item {
  position: relative;
  overflow: visible;
}

.faq-item::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 1rem;
  padding: 2px;
  background: linear-gradient(135deg, transparent, rgba(19, 117, 71, 0.3), transparent);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.faq-item:hover::before {
  opacity: 1;
}

/* Glow effect when FAQ is open */
.faq-item.border-\[\#EDD97A\] {
  box-shadow: 0 0 0 1px rgba(176, 140, 122, 0.1), 
              0 8px 16px -4px rgba(19, 117, 71, 0.2);
}

/* Smooth icon rotation and color transition */
.faq-item svg {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Add subtle pulse animation to open FAQ icon */
@keyframes faqPulse {
  0%, 100% {
    transform: scale(1.1);
  }
  50% {
    transform: scale(1.15);
  }
}

.faq-item .scale-110 {
  animation: faqPulse 2s ease-in-out infinite;
}

/* Smooth background gradient on hover */
.faq-item button {
  position: relative;
  overflow: hidden;
}

.faq-item button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(19, 117, 71, 0.05), transparent);
  transition: left 0.5s ease;
}

.faq-item:hover button::before {
  left: 100%;
}
</style>


